<template>
	<a
		@click="onClick"
		class="cursor-pointer select-none inline-flex items-start gap-2"
		:aria-sort="ariaSort"
		role="button"
	>
		<span :class="labelClass"><slot></slot></span>

		<span class="sort-arrow-group">
			<!-- Active column: show only the active direction chevron; Inactive column: show both outline chevrons -->
			<template v-if="isActive">
				<template v-if="upIsActive">
					<ArrowLongUpIcon class="sort-arrow-up" :class="activeIconClass" />
					<ArrowLongDownIcon class="sort-arrow-down" :class="inactiveIconClass" />
				</template>
				<template v-else>
					<ArrowLongUpIcon class="sort-arrow-up" :class="inactiveIconClass" />
					<ArrowLongDownIcon class="sort-arrow-down" :class="activeIconClass" />
				</template>
			</template>
			<template v-else>
				<ArrowLongUpIcon class="sort-arrow-up" :class="inactiveIconClass" />
				<ArrowLongDownIcon class="sort-arrow-down" :class="inactiveIconClass" />
			</template>

			<span class="sr-only" v-if="isActive">Sorted {{ sortDirectionText }}</span>
		</span>
	</a>

</template>

<style scoped>
.sort-arrow-group {
	@apply relative flex items-start;
}
.sort-arrow-up {
	@apply absolute h-[0.9rem];
}
.sort-arrow-down {
	@apply absolute h-[0.9rem] left-1.5;
}
</style>

<script lang="ts">
import { OrderDirection, OrderParameter } from "@/models/OrderParameter.model";
import { ArrowLongDownIcon, ArrowLongUpIcon } from "@heroicons/vue/24/outline";

export default {

	data() {
		return {
			localOrder: null as OrderParameter | null
		};
	},

	props: {
		order: {
			type: OrderParameter,
			required: true
		},

		property: {
			type: String,
			required: true
		},

		defaultDirection: {
			type: String,
			default: OrderDirection.ASC
		}
	},

	components: {
		ArrowLongDownIcon, ArrowLongUpIcon,
	},

	created() {
		this.localOrder = this.order;
	},

	computed: {
		isActive() {
			return this.localOrder?.property === this.property;
		},

		ariaSort(): "ascending" | "descending" | "none" {
			if (!this.isActive) return "none";
			return this.localOrder?.direction === OrderDirection.ASC ? "ascending" : "descending";
		},

		labelClass(): string {
			// Keep weight consistent to avoid width shifts; only change color
			return this.isActive ? "text-indigo-700" : "";
		},

		upIsActive(): boolean {
			return this.isActive && this.localOrder?.direction === OrderDirection.ASC;
		},

		downIsActive(): boolean {
			return this.isActive && this.localOrder?.direction === OrderDirection.DESC;
		},

		// Active icon is colored, inactive is neutral gray
		activeIconClass(): string {
			return "text-indigo-700";
		},

		inactiveIconClass(): string {
			return this.isActive ? "text-indigo-700 opacity-30" : "opacity-50";
		},

		sortDirectionText(): string {
			if (!this.isActive) return "";
			return this.localOrder?.direction === OrderDirection.ASC ? "ascending" : "descending";
		},
	},

	methods: {
		onClick() {
			if (this.order.property === this.property) {
				this.order.toggleDirection();
			} else {
				this.order.property = this.property;

				// Looks silly, but since properties can only be strings we need to cast the default direction to the enum value
				switch(this.defaultDirection) {
					case 'asc':
						this.order.direction = OrderDirection.ASC;
						break;
					case 'desc':
						this.order.direction = OrderDirection.DESC;
						break;
					default:
						this.order.direction = OrderDirection.ASC;
				}
			}
			this.$emit('update:modelValue', this.order);
			this.$emit('change', this.order);
		}
	}

}
</script>
